import { createApi } from '@reduxjs/toolkit/query/react';
import {
  FlowResponse,
  AttendeesResponse,
  AttendeesOptionsResponse,
  AttendeesFilters,
  AutomationFilters,
  Sort,
  GetFlowsResponse,
  FlowExtended,
  FlowStatusUpdateFilters,
  ScriptsFilters,
  ScriptsResponse,
  ScriptResponse,
  AutomationAppVariablesRequest,
  AutomationAppsResponse,
  GetAttendeeByIdResponse,
  GetAttendeeMessagesResponse,
} from 'lib.store/src/type-partner/automation/models';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
export const IDS = 'ids';
const getQueryStringFromParams = (params: string[] | number[] = [], key: string) => {
  if (Array.isArray(params)) {
    const filtersKey = key ? key.replace('.', '][') : key;
    return params
      .map((id) => {
        const encodeId = encodeURIComponent(id || '');
        return `filters[${filtersKey}][]=${encodeId}`;
      })
      .join('&');
  }
  return '';
};
export type ActionInputItem = {
  type: ActionInputTypes;
  name: string;
  label: string;
  condition?: string;
  value: ActionInputTypeValues[ActionInputTypes];
  values?: {
    fetch: string;
    values: {
      name: string;
      value: string;
    }[];
    multi: boolean;
  };
  variables?: ActionVariableItem[];
};
enum ActionInputTypes {
  input = 'input',
  checkbox = 'checkbox',
  select = 'select',
  textarea = 'textarea',
}
type ActionInputTypeValues = {
  [ActionInputTypes.input]: string[];
  [ActionInputTypes.checkbox]: boolean;
  [ActionInputTypes.select]: string[];
  [ActionInputTypes.textarea]: string;
};
type ActionVariableItem = {
  value: string;
  label: string;
  name: string;
  description: string;
};

const automation = `automation`;
const automation_flows = `automation_flows`;
const automation_attendees = `automation_attendees`;
const automation_attendee_info = `automation_attendee_info`;

export const automationApi = createApi({
  reducerPath: automation,
  baseQuery: baseQueryWithReauth,
  tagTypes: [automation_flows, automation_attendees, automation_attendee_info],
  endpoints: (build) => ({
    postFlow: build.mutation<FlowResponse, FlowExtended>({
      query: (data) => ({
        url: '/partner/automation/flows',
        method: 'POST',
        body: data,
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: automation_flows }],
    }),
    getFlowById: build.query<FlowResponse, { flowId: string }>({
      query: ({ flowId }) => ({
        url: `/partner/automation/flows/${flowId}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { flowId }) => [{ type: automation_flows, id: flowId }],
    }),
    patchFlow: build.mutation<FlowResponse, { flowId: string; data: FlowExtended }>({
      query: ({ flowId, data }) => ({
        url: `/partner/automation/flows/${flowId}`,
        method: 'PATCH',
        body: data,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: automation_flows, id: 'LIST' }],
    }),
    patchFlowStatus: build.mutation<
      void,
      {
        statusId: '0' | '1' | '2' | '3';
        filters: Partial<FlowStatusUpdateFilters>;
      }
    >({
      query: ({ statusId, filters }) => {
        const params = new URLSearchParams();
        const { search, statuses, events, ids } = filters;

        const idsQueryString = getQueryStringFromParams(ids, 'ids');
        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const statusesQueryString = getQueryStringFromParams(statuses, 'statuses');

        if (search) {
          params.append('filters[search]', `${search}`);
        }
        const filterQueryString = params.toString();

        const queryString = [
          idsQueryString,
          eventsQueryString,
          statusesQueryString,
          filterQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/partner/automation/flows/status/${statusId}${queryString.length ? `/?${queryString}` : ''}`,
          method: 'PATCH',
          body: filters,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };
      },
      invalidatesTags: [{ type: automation_flows, id: 'LIST' }],
    }),
    getFlows: build.query<
      GetFlowsResponse,
      { filters: AutomationFilters; sort?: Sort[]; page: number }
    >({
      query: ({ filters, sort = [], page }) => {
        const params = new URLSearchParams();
        const { search, ids, events, statuses } = filters;

        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const idsQueryString = getQueryStringFromParams(ids, 'ids');
        const statusesQueryString = getQueryStringFromParams(statuses, 'statuses');

        if (search) {
          params.append('filters[search]', `${search}`);
        }
        if (page) {
          params.append('page', `${page}`);
        }
        sort.forEach(({ field, direction }) => {
          params.append('sort.*.field', field);
          params.append('sort.*.direction', direction);
        });

        const filterQueryString = params.toString();

        const queryString = [
          idsQueryString,
          eventsQueryString,
          filterQueryString,
          statusesQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/partner/automation/flows${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: automation_flows, id: 'LIST' }],
    }),
    getAttendees: build.query<
      AttendeesResponse,
      { filters: AttendeesFilters; sort: Sort[]; page: number }
    >({
      query: ({ filters, sort: _sort, page }) => {
        const params = new URLSearchParams();

        const { search, ids, events, titles, companies, names } = filters;

        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const idsQueryString = getQueryStringFromParams(ids, 'ids');
        const namesQueryString = getQueryStringFromParams(names, 'names');
        if (search) {
          params.append('filters[search]', `${search}`);
        }
        if (page) {
          params.append('page', `${page}`);
        }
        const titlesQueryString = getQueryStringFromParams(titles, 'titles');
        const companiesQueryString = getQueryStringFromParams(companies, 'companies');
        // sort.forEach(({ field, direction }) => {
        //     params.append('sort.*.field', field);
        //     params.append('sort.*.direction', direction);
        // });

        const filterQueryString = params.toString();

        const queryString = [
          idsQueryString,
          eventsQueryString,
          titlesQueryString,
          companiesQueryString,
          namesQueryString,
          filterQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/partner/automation/attendees${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),

    patchAttendeesStatus: build.mutation<
      void,
      { statusId: '0' | '1' | '2' | '3'; filters: Partial<AttendeesFilters> }
    >({
      query: ({ statusId, filters }) => ({
        url: `/partner/automation/attendees/status/${statusId}`,
        method: 'PATCH',
        body: filters,
      }),
      invalidatesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),

    getAutomationApps: build.query<
      AutomationAppsResponse,
      { filters: AttendeesFilters; sort?: Sort[] }
    >({
      query: ({ filters, sort: _sort = [] }) => {
        const params = new URLSearchParams();

        const { search, ids, events, titles, companies, names } = filters;

        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const idsQueryString = getQueryStringFromParams(ids, 'ids');
        const namesQueryString = getQueryStringFromParams(names, 'names');
        const titlesQueryString = getQueryStringFromParams(titles, 'titles');
        const companiesQueryString = getQueryStringFromParams(companies, 'companies');

        if (search) {
          params.append('filters[search]', `${search}`);
        }

        const filterQueryString = params.toString();

        const queryString = [
          idsQueryString,
          eventsQueryString,
          filterQueryString,
          namesQueryString,
          titlesQueryString,
          companiesQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/partner/automation/apps${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),
    patchAutomationAppVariables: build.mutation<any, AutomationAppVariablesRequest>({
      query: ({ appId, variables }) => ({
        url: `/partner/automation/apps/${appId}/variables`,
        method: 'PATCH',
        body: {
          variables: variables.map((variable) => ({
            name: variable.name,
            value: variable.value,
            encrypt: 0,
          })),
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),

    getAttendiesFilterSuggestions: build.query<
      AttendeesResponse,
      { enum: 'events' | 'titles' | 'companies'; filters: Partial<AttendeesFilters> }
    >({
      query: ({ enum: enumValue, filters }) => {
        const params = new URLSearchParams();

        const { search, ids, events, titles, companies, names } = filters;

        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const idsQueryString = getQueryStringFromParams(ids, 'ids');

        if (search) {
          params.append('filters[search]', `${search}`);
        }
        const titlesQueryString = getQueryStringFromParams(titles, 'titles');
        const companiesQueryString = getQueryStringFromParams(companies, 'companies');
        const namesQueryString = getQueryStringFromParams(names, 'names');
        const filterQueryString = params.toString();

        const queryString = [
          idsQueryString,
          eventsQueryString,
          titlesQueryString,
          companiesQueryString,
          namesQueryString,
          filterQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/partner/automation/attendees/filter/${enumValue}${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
    }),

    getAttendeesWithFilter: build.query<
      AttendeesResponse,
      {
        url: string;
        filters: ActionInputItem[];
        sort?: Sort[];
        page: number;
        isPreview?: boolean;
        isAppendIdsFilter?: boolean;
      }
    >({
      query: ({ url = '', filters, page, isPreview, isAppendIdsFilter }) => {
        const params = new URLSearchParams();

        const selectsQueryString: string[] = [];

        filters.forEach((filter) => {
          if (filter.name === IDS && (isPreview || isAppendIdsFilter)) {
            selectsQueryString.push(
              getQueryStringFromParams(filter.value as string[], filter.name),
            );
            return;
          }
          if (filter.name === IDS && !isPreview) {
            return;
          }
          if (!isPreview) {
            switch (filter.type) {
              case ActionInputTypes.select:
                selectsQueryString.push(
                  getQueryStringFromParams(filter.value as string[], filter.name),
                );
                break;
              case ActionInputTypes.input:
              case ActionInputTypes.checkbox:
              case ActionInputTypes.textarea: {
                params.append(
                  `filters[${filter.name}]`,
                  filter.value ? `${filter.value as string}` : '',
                );
                break;
              }
              default:
                return;
            }
          }
        });

        if (page) {
          params.append('page', `${page}`);
        }

        const filterQueryString = params.toString();

        const queryString = [...selectsQueryString, filterQueryString].filter(Boolean).join('&');

        return {
          url: `${url.replace('/api', '')}${queryString.length ? `&${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),
    postAttendeesWithFilter: build.query<
      AttendeesResponse,
      {
        url: string;
        filters: ActionInputItem[];
        sort?: Sort[];
        page: number;
        isPreview?: boolean;
        isAppendIdsFilter?: boolean;
        ids_exclude?: string[];
      }
    >({
      query: ({ url = '', filters, page, isPreview, isAppendIdsFilter, ids_exclude = [] }) => {
        const params = new URLSearchParams();

        const mappedFilters: Record<string, string | string[] | number[] | boolean> = {
          ids_exclude,
        };

        const selectsQueryString: string[] = [];

        filters.forEach((filter) => {
          if (filter.name === IDS && (isPreview || isAppendIdsFilter)) {
            selectsQueryString.push(
              getQueryStringFromParams(filter.value as string[], filter.name),
            );
            return;
          }
          if (filter.name === IDS && !isPreview) {
            return;
          }
          if (!isPreview) {
            switch (filter.type) {
              case ActionInputTypes.select:
                selectsQueryString.push(
                  getQueryStringFromParams(filter.value as string[], filter.name),
                );
                break;
              case ActionInputTypes.input:
              case ActionInputTypes.checkbox:
              case ActionInputTypes.textarea: {
                params.append(
                  `filters[${filter.name}]`,
                  filter.value ? `${filter.value as string}` : '',
                );
                break;
              }
              default:
                return;
            }
          }
        });

        if (page) {
          params.append('page', `${page}`);
        }

        const filterQueryString = params.toString();

        const queryString = [...selectsQueryString, filterQueryString].filter(Boolean).join('&');

        return {
          url: `${url.replace('/api', '')}${queryString.length ? `&${queryString}` : ''}`,
          method: 'post',
          headers: {
            Accept: 'application/json',
          },
          body: {
            ...mappedFilters,
          },
        };
      },
      providesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),
    getAttendeesFilterSelectOptions: build.query<
      AttendeesOptionsResponse,
      { url: string; page: number }
    >({
      query: ({ url = '', page }) => {
        const params = new URLSearchParams();

        if (page) {
          params.append('page', `${page}`);
        }

        const queryString = params.toString();

        return {
          url: `${url.replace('/api', '')}${queryString.length ? `&${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),
    getScripts: build.query<ScriptsResponse, { filters: ScriptsFilters }>({
      query: ({ filters }) => {
        const { statuses, apps } = filters;

        const statusesQueryString = getQueryStringFromParams(statuses, 'statuses');
        const appsQueryString = getQueryStringFromParams(apps, 'apps');

        const queryString = [statusesQueryString, appsQueryString].filter(Boolean).join('&');

        return {
          url: `/partner/automation/scripts${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: automation_attendees, id: 'LIST' }],
    }),
    getScriptById: build.query<ScriptResponse, { scriptId: string }>({
      query: ({ scriptId }) => ({
        url: `/partner/automation/scripts/${scriptId}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { scriptId }) => [{ type: automation_flows, id: scriptId }],
    }),
    postRunFlow: build.mutation<FlowResponse, { flowId: string }>({
      query: ({ flowId }) => ({
        url: `/partner/automation/flows/${flowId}/run`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: automation_flows }],
    }),
    getAttendeeById: build.query<GetAttendeeByIdResponse, { id: string }>({
      query: ({ id }) => ({
        url: `partner/automation/attendees/${id}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { id }) => [{ type: automation_attendee_info, id }],
    }),
    postAttendeeTagByAttendeeId: build.mutation<void, { id: string; tagList: string[] }>({
      query: ({ id, tagList }) => ({
        url: `partner/automation/attendees/${id}/tags`,
        method: 'POST',
        body: {
          tags: tagList,
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: automation_attendee_info, id },
        { type: automation_attendees, id: 'LIST' },
      ],
    }),
    deleteAttendeeTagByAttendeeId: build.mutation<void, { id: string; tagList: string[] }>({
      query: ({ id, tagList }) => ({
        url: `partner/automation/attendees/${id}/tags`,
        method: 'DELETE',
        body: {
          tags: tagList,
        },
      }),
    }),
    postConnectApp: build.mutation<void, { appId: string }>({
      query: ({ appId }) => ({
        url: `/partner/automation/apps/${appId}/connect`,
        method: 'POST',
      }),
    }),
    getAttendeeMessagesById: build.query<GetAttendeeMessagesResponse, { id: string }>({
      query: ({ id }) => ({
        url: `partner/automation/messages?filters[attendees][]=${id}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { id }) => [{ type: automation_attendee_info, id }],
    }),
  }),
});

export const {
  usePostFlowMutation,
  usePostRunFlowMutation,
  useGetFlowByIdQuery,
  useLazyGetFlowByIdQuery,
  usePatchFlowMutation,
  usePatchFlowStatusMutation,
  useGetFlowsQuery,
  useGetAttendeesQuery,
  useGetAttendeesWithFilterQuery,
  usePostAttendeesWithFilterQuery,
  useGetAttendiesFilterSuggestionsQuery,
  useGetAutomationAppsQuery,
  usePatchAutomationAppVariablesMutation,
  useGetAttendeesFilterSelectOptionsQuery,
  useGetScriptsQuery,
  useLazyGetScriptsQuery,
  useGetScriptByIdQuery,
  useLazyGetScriptByIdQuery,
  useGetAttendeeByIdQuery,
  usePostAttendeeTagByAttendeeIdMutation,
  useDeleteAttendeeTagByAttendeeIdMutation,
  usePostConnectAppMutation,
  useGetAttendeeMessagesByIdQuery,
} = automationApi;
